import { S25Util } from "../../../../util/s25-util";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";

import {
    DEFAULT_WINDOW_MODE,
    DURATION_WINDOW_MODES,
    durationNameMap,
    FIXED_WINDOW_MODES,
    MAX_FIXED_WINDOW_DAYS,
    MAX_ROLLING_DURATION_BY_UNIT,
    windowModeNameMap,
    WindowMode,
} from "./event-quota.model";
import type { Detailed } from "./event-quota.model";

export function toQuotaTypeId(typeId: string | number | undefined): number | undefined {
    if (typeId == null || typeId === "") {
        return undefined;
    }
    return S25Util.toInt(typeId);
}

export function hasMeaningfulMaxTimeHoursMinutes(
    hours: number | null | undefined,
    minutes: number | null | undefined,
): boolean {
    return (hours ?? 0) > 0 || (minutes ?? 0) > 0;
}

export function normalizeWindowMode(mode: string | undefined | null): WindowMode {
    if (mode && mode in windowModeNameMap) {
        return mode as WindowMode;
    }
    return DEFAULT_WINDOW_MODE;
}

export function windowModeLabel(mode: WindowMode | undefined | null): string {
    return windowModeNameMap[normalizeWindowMode(mode)];
}

export function usesDurationForWindow(mode: WindowMode | undefined | null): boolean {
    return DURATION_WINDOW_MODES.includes(normalizeWindowMode(mode));
}

export function usesFixedDatesForWindow(mode: WindowMode | undefined | null): boolean {
    return FIXED_WINDOW_MODES.includes(normalizeWindowMode(mode));
}

function durationUnitInFutureLabel(durationType: string | undefined, duration: number | undefined): string {
    const count = duration ?? 0;
    const plural = count === 1 ? "" : "s";
    switch (durationType) {
        case "M":
            return `month${plural} in the future`;
        case "Y":
            return `year${plural} in the future`;
        case "W":
            return `week${plural} in the future`;
        case "D":
        default:
            return `day${plural} in the future`;
    }
}

function formatWindowDateLabel(serverDate: string | undefined | null, dateFormat: string | undefined | null): string {
    if (!serverDate) {
        return "";
    }
    if (dateFormat) {
        return S25Datefilter.transform(serverDate, dateFormat) ?? "";
    }
    return formDateFromServer(serverDate);
}

export function windowDateLabels(
    quota: {
        windowMode?: WindowMode | null;
        quotaStartDt?: string | null;
        quotaEndDt?: string | null;
    },
    dateFormat: string,
): { windowStartLabel?: string; windowEndLabel?: string } {
    const windowMode = normalizeWindowMode(quota.windowMode);
    if (!usesFixedDatesForWindow(windowMode)) {
        return {};
    }
    const windowEndLabel = quota.quotaEndDt ? S25Datefilter.transform(quota.quotaEndDt, dateFormat) : undefined;
    let windowStartLabel: string | undefined;
    if (windowMode === "fixed") {
        windowStartLabel = quota.quotaStartDt ? S25Datefilter.transform(quota.quotaStartDt, dateFormat) : undefined;
    } else {
        windowStartLabel = S25Datefilter.transform(new Date(), dateFormat);
    }
    return { windowStartLabel, windowEndLabel };
}

export function timeWindowLabel(
    quota: {
        windowMode?: WindowMode | null;
        duration?: number | null;
        durationType?: string | null;
        durationTypeName?: string | null;
        quotaStartDt?: string | null;
        quotaEndDt?: string | null;
    },
    dateFormat?: string | null,
): string {
    const windowMode = normalizeWindowMode(quota.windowMode);
    if (usesFixedDatesForWindow(windowMode)) {
        const endDate = formatWindowDateLabel(quota.quotaEndDt, dateFormat);
        if (windowMode === "today") {
            if (endDate) {
                if (S25Util.date.isAfter(endDate)) {
                    return `Ended ${endDate}`;
                } else {
                    return `Current Date - ${endDate}`;
                }
            } else {
                return windowModeLabel(windowMode);
            }
        }
        const startDate = formatWindowDateLabel(quota.quotaStartDt, dateFormat);
        if (startDate && endDate) {
            return `${startDate} - ${endDate}`;
        }
        if (endDate) {
            return `-> ${endDate}`;
        }
        return windowModeLabel(windowMode);
    }
    const duration = quota.duration ?? 0;
    if (windowMode === "future") {
        return `${duration} ${durationUnitInFutureLabel(quota.durationType ?? undefined, duration)} (rolling future)`;
    }
    const durationTypeName = quota.durationTypeName ?? durationNameMap.D + (duration !== 1 ? "s" : "");
    return `${duration} ${durationTypeName} (rolling)`;
}

export function hasCountLimit(quota: Pick<Detailed, "quantity"> | undefined | null): boolean {
    return (quota?.quantity ?? 0) > 0;
}

export function hasTimeLimit(quota: Pick<Detailed, "maxTime"> | undefined | null): boolean {
    return S25Util.ISODurationToMinutes(quota?.maxTime ?? "") > 0;
}

export function durationForPayload(
    windowMode: WindowMode | undefined | null,
    duration: number | undefined | null,
): number {
    return usesDurationForWindow(windowMode) ? (duration ?? 0) : 0;
}

export function maxRollingDurationForUnit(durationType: string | undefined): number | undefined {
    if (durationType === "D" || durationType === "W" || durationType === "M" || durationType === "Y") {
        return MAX_ROLLING_DURATION_BY_UNIT[durationType];
    }
    return undefined;
}

export function rollingDurationLimitMessage(durationType: string | undefined): string {
    switch (durationType) {
        case "W":
            return `Period length cannot exceed ${MAX_ROLLING_DURATION_BY_UNIT.W} weeks.`;
        case "M":
            return `Period length cannot exceed ${MAX_ROLLING_DURATION_BY_UNIT.M} months.`;
        case "Y":
            return `Period length cannot exceed ${MAX_ROLLING_DURATION_BY_UNIT.Y} years.`;
        case "D":
        default:
            return `Period length cannot exceed ${MAX_ROLLING_DURATION_BY_UNIT.D} days.`;
    }
}

export function rollingPeriodLengthHelpText(): string {
    const limits = MAX_ROLLING_DURATION_BY_UNIT;
    return `Maximum: ${limits.D} days, ${limits.W} weeks, ${limits.M} months, or ${limits.Y} years.`;
}

export function fixedWindowSpanHelpText(): string {
    return `Date range cannot exceed ${MAX_FIXED_WINDOW_DAYS} days.`;
}

export function fixedWindowSpanDays(start: Date, end: Date): number {
    return S25Util.date.diffDays(start, end);
}

export function exceedsFixedWindowSpanLimit(start: Date, end: Date): boolean {
    return fixedWindowSpanDays(start, end) > MAX_FIXED_WINDOW_DAYS;
}

export function exceedsForwardFromTodaySpanLimit(end: Date, asOfDate: Date = S25Util.date.currentDate()): boolean {
    return fixedWindowSpanDays(asOfDate, end) > MAX_FIXED_WINDOW_DAYS;
}

export function formDateFromServer(serverDate: string | undefined | null): string {
    if (!serverDate) {
        return "";
    }
    const parsed = S25Util.date.parse(serverDate);
    if (!parsed) {
        return "";
    }
    return S25Util.date.toS25ISODateTimeStr(parsed)?.slice(0, 10) ?? "";
}

export function formPickerDateFromServer(serverDate: string | undefined | null): Date | null {
    const formDate = formDateFromServer(serverDate);
    if (!formDate) {
        return null;
    }
    const parsed = S25Util.date.parseDropTZ(formDate);
    return parsed && !isNaN(parsed.getTime()) ? parsed : null;
}

export function serverDateFromForm(formDate: string | Date | null | undefined): string | undefined {
    if (formDate == null) {
        return undefined;
    }
    return S25Util.date.toS25ISODateTimeStr(formDate);
}
